import { useQuery } from '@tanstack/react-query'
import { FileText, FolderPlus, Images, Package, Plus, WifiOff } from 'lucide-react'
import type { LucideProps } from 'lucide-react'
import type { ComponentType } from 'react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { CreateProjectDialog } from './CreateProjectDialog'
import { TrashSection } from './TrashSection'

import { api } from '../../api/client'
import type { ProjectSummary } from '../../api/types.gen'
import { formatBytes } from '../../lib/format'
import { usePageTitle } from '../../lib/usePageTitle'
import {
  Badge,
  Button,
  Card,
  CardsSkeleton,
  EmptyState,
  ErrorState,
  ICON_STROKE,
  type Tone,
} from '../../ui'

/**
 * El listado devuelve el resumen, no el proyecto entero: sin `description`,
 * que en una retícula de 25 filas serían hasta 50 KB que nadie lee.
 */
type Project = ProjectSummary

/*
 | Los estados del proyecto, traducidos.
 |
 | Antes se pintaba `{project.status}` en crudo, así que en el panel de un
 | fotógrafo peruano ponía «active». El color no es decorativo: `archived` en
 | gris y `active` en verde se distinguen de un vistazo en una rejilla de
 | treinta proyectos, que es como se usa esta pantalla.
 */
const STATUS: Record<string, { label: string; tone: Tone }> = {
  active: { label: 'Activo', tone: 'positive' },
  archived: { label: 'Archivado', tone: 'neutral' },
  draft: { label: 'Borrador', tone: 'neutral' },
}

export function ProjectsPage() {
  usePageTitle('Proyectos')
  const [creating, setCreating] = useState(false)
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<{ data: Project[]; next_cursor: string | null }>('/projects?limit=25'),
  })

  return (
    <section>
      <header className="mb-5 flex items-center justify-between gap-4">
        <h1 className="text-title font-semibold text-ink">Proyectos</h1>
        <Button onClick={() => setCreating(true)}>
          <Plus size={17} strokeWidth={2} aria-hidden />
          Nuevo proyecto
        </Button>
      </header>

      {isPending ? (
        <CardsSkeleton />
      ) : error ? (
        <ErrorState
          icon={WifiOff}
          title="No se pudieron cargar tus proyectos"
          description="Casi siempre es la conexión, no nosotros. Vuelve a intentarlo."
          action={
            <Button variant="secondary" onClick={() => void refetch()}>
              Reintentar
            </Button>
          }
        />
      ) : data.data.length === 0 ? (
        // El vacío de una lista es el mejor sitio para enseñar cómo se empieza:
        // es el único momento en que se tiene toda la atención del usuario.
        <EmptyState
          icon={FolderPlus}
          title="Aún no tienes ningún proyecto"
          description="Un proyecto es una entrega: una boda, una sesión, un rodaje. Dentro subes los archivos y desde ahí compartes el enlace con tu cliente."
          action={
            <Button onClick={() => setCreating(true)}>
              <Plus size={17} strokeWidth={2} aria-hidden />
              Crear mi primer proyecto
            </Button>
          }
        />
      ) : (
        // Tarjetas en móvil, tabla en escritorio. Una tabla con scroll
        // horizontal en el móvil es el patrón que más se sufre en este tipo de
        // producto: se pierde la primera columna y deja de entenderse la fila.
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {sinArchivarPrimero(data.data).map((project) => {
            const status = STATUS[project.status] ?? { label: project.status, tone: 'neutral' as Tone }

            return (
              <li key={project.id}>
                <Link to={`/projects/${project.id}`} className="block h-full rounded-card">
                  <Card interactive className="flex h-full flex-col gap-2 p-4">
                    <h2 className="flex items-center gap-2 text-subhead font-medium text-ink">
                      {/* El icono lo pone el CONTENIDO, no un campo que nadie
                          rellenó: `projects.type` se deduce de lo que hay dentro
                          (ver ProjectType en el backend). Así una entrega de
                          planos se distingue de una boda de un vistazo, sin
                          haberle preguntado nada al estudio al crearla. */}
                      <TypeIcon type={project.type} />
                      <span className="truncate">{project.name}</span>
                    </h2>

                    <p className="text-body text-ink-muted" data-numeric>
                      {project.asset_count} {TYPE_NOUN[project.type] ?? 'archivos'} ·{' '}
                      {formatBytes(project.storage_bytes)}
                    </p>

                    <Badge tone={status.tone} className="mt-auto self-start">
                      {status.label}
                    </Badge>
                  </Card>
                </Link>
              </li>
            )
          })}
        </ul>
      )}

      {/* Sólo se pinta si hay algo dentro: es la red de un borrado, no una
          sección del producto. */}
      <TrashSection />

      {/* Montado sólo mientras está abierto, para que empiece limpio. */}
      {creating && <CreateProjectDialog onClose={() => setCreating(false)} />}
    </section>
  )
}

/**
 * Lo archivado, al final.
 *
 * El diálogo de archivar promete «para que no te estorbe en la lista», y sin
 * esto la promesa era falsa: el proyecto se quedaba exactamente donde estaba,
 * sólo que con otra insignia.
 *
 * Se ordena aquí y no en el servidor porque la paginación va por cursor sobre
 * `created_at`: reordenar en SQL obligaría a rehacer el cursor para no repetir
 * ni saltarse filas. Con 25 por página, ordenar la página que se tiene delante
 * hace lo que el estudio espera.
 */
function sinArchivarPrimero(projects: Project[]): Project[] {
  return [...projects].sort(
    (a, b) => Number(a.status === 'archived') - Number(b.status === 'archived'),
  )
}

/*
 | El vocabulario cambia con el tipo, y es media razón de ser de P4.
 |
 | «300 fotos» es correcto en una boda y raro en una entrega de planos. Decirle
 | «fotos» a un arquitecto es el mismo error que llamar «álbum» a un proyecto:
 | le cuenta que este producto no es para él.
 */
const TYPE_NOUN: Record<string, string> = {
  gallery: 'fotos',
  documents: 'documentos',
  delivery: 'archivos',
  video_review: 'archivos',
}

const TYPE_ICON: Record<string, ComponentType<LucideProps>> = {
  gallery: Images,
  documents: FileText,
  delivery: Package,
  video_review: Package,
}

function TypeIcon({ type }: { type: string }) {
  const Icon = TYPE_ICON[type] ?? Package

  return <Icon size={17} strokeWidth={ICON_STROKE} className="shrink-0 text-ink-subtle" aria-hidden />
}
